









































// eslint-disable-next-line no-duplicate-imports
import {
  computed,
  defineComponent,
  inject,
  onMounted,
  ssrRef,
  useContext,
  useFetch,
  watch
} from '@nuxtjs/composition-api';
import { SfLoader } from '@storefront-ui/vue';
import VaimoProductCard from 'organisms/VaimoProductCard.vue';

import { useImage, useProduct } from '~/composables';
import VaimoGrid from '~/diptyqueTheme/components/molecules/VaimoGrid.vue';
import VaimoSlider from '~/diptyqueTheme/components/molecules/VaimoSlider.vue';
import {
  useAlternativeVisual,
  useGoogleTagManager,
  useRequestPath
} from '~/diptyqueTheme/composable';
import customProductGetters from '~/diptyqueTheme/helpers/productGetters';
import type {
  DescriptionOption,
  Visual
} from '~/diptyqueTheme/types/contentful';
import { DESC_SETTING } from '~/diptyqueTheme/types/contentful/constants/injectionKeys.const';
import { Logger } from '~/helpers/logger';
import type { RecommendParams } from '~/integrations/algolia/types/recommend';
import type { ProductsGroupModelFields } from '~/integrations/contentful/src/types';
import {
  useAlgoliaInsights,
  useAlgoliaRecommend
} from '~/modules/algolia-search/composables';
import productGetters from '~/modules/catalog/product/getters/productGetters';
import type { Product } from '~/modules/catalog/product/types';

type TDisplayOption = 'slider' | 'grid';
interface VaimoProductsProps {
  uniqueKey?: string;
  skuList?: string[];
  dataProvider?: ProductsGroupModelFields['type'];
  display?: TDisplayOption;
  wrapperProps?: Record<TDisplayOption, unknown>;
  pageSize?: number;
  loadingType?: 'spinner';
  gridColsMobile?: number;
  gridColsDesktop?: number;
  altVisuals?: Visual[];
  getProductsByCategory?: boolean;
  categoryData?: { categoryID: string; limit: number };
  recommendations: RecommendParams;
}

export default defineComponent<VaimoProductsProps>({
  name: 'VaimoProducts',
  components: {
    VaimoProductCard,
    VaimoSlider,
    VaimoGrid,
    SfLoader
  },
  props: {
    uniqueKey: {
      required: false,
      type: String,
      default: 'VaimoProducts'
    },
    skuList: {
      required: false,
      type: Array,
      default: () => []
    },
    dataProvider: {
      required: false,
      type: String,
      default: 'list of sku'
    },
    display: {
      required: false,
      type: String,
      default: 'slider',
      validator: (val: string) => ['slider', 'grid'].includes(val)
    },
    wrapperProps: {
      required: false,
      type: Object,
      default: () => ({})
    },
    pageSize: {
      type: Number,
      required: false,
      default: 20
    },
    loadingType: {
      required: false,
      type: String,
      default: 'spinner'
    },
    gridColsMobile: {
      type: Number,
      required: false,
      default: 2
    },
    gridColsDesktop: {
      type: Number,
      required: false,
      default: 4
    },
    altVisuals: {
      type: Array,
      required: false,
      default: () => [] as Visual[]
    },
    getProductsByCategory: Boolean,
    categoryData: {
      required: false,
      type: Object,
      default: () => ({})
    },
    recommendations: {
      default: () => ({}),
      type: Object
    }
  },
  emits: ['productsLoaded'],
  setup(props, { emit }) {
    const { app } = useContext();
    const { getViewItemListDetails, getSelectItemDetails } =
      useGoogleTagManager();
    const { getMagentoImage } = useImage();
    const { getSliderProductsList, loading: productsLoading } = useProduct();
    const { getRelatedProductUrl } = useRequestPath();
    const { applyAltVisuals } = useAlternativeVisual();
    const { fetchRecommendations, recommendations } = useAlgoliaRecommend();
    const { sendClickedObjectIDs } = useAlgoliaInsights();

    const SKUs = computed(() => props.skuList);
    const products = ssrRef([], props.uniqueKey);
    const error = ssrRef(null, `${props.uniqueKey}-error`);

    const hideDescriptionOptions = inject<DescriptionOption[]>(
      DESC_SETTING,
      []
    );

    const trackViewEvent = () => {
      if (products.value.length) {
        app.$gtm.push(getViewItemListDetails(products.value, 'slider'));
      }
    };

    const fetchProductsList = async (
      filter: Record<string, unknown>,
      limit: number
    ) => {
      const data = await getSliderProductsList({
        filter,
        pageSize: limit
      });
      return data?.items || [];
    };

    const hasProductsChanged = () => {
      if (!products.value.length) return true;
      const loadedSKUs = products.value.map((p) => p.sku).sort();
      const currentSKUs = [...(SKUs.value || [])].sort();
      return JSON.stringify(loadedSKUs) !== JSON.stringify(currentSKUs);
    };

    const sortProductsBySku = (productsList: Product[], skuOrder: string[]) => {
      return [...productsList].sort(
        (a, b) => skuOrder.indexOf(a.sku) - skuOrder.indexOf(b.sku)
      );
    };

    const transformProducts = (productsList: Product[], skuOrder: string[]) => {
      const sorted = sortProductsBySku(productsList, skuOrder);
      return props.altVisuals.length
        ? applyAltVisuals(sorted, props.altVisuals)
        : sorted;
    };

    const getProductsByCategoryID = async (
      categoryID: string,
      limit: number
    ) => {
      return fetchProductsList(
        {
          category_id: {
            eq: categoryID
          }
        },
        limit
      );
    };

    const getProductsBySku = async (skuArray: string[], limit: number) => {
      const items = await fetchProductsList(
        {
          sku: {
            in: skuArray
          }
        },
        limit
      );

      if (!items.length) {
        return [];
      }

      return transformProducts(items as Product[], skuArray);
    };

    const productFetchers = {
      'algolia-recommend': async () => {
        if (!props.recommendations?.model) return [];

        await fetchRecommendations(
          props.recommendations.model,
          props.recommendations
        );
        return [...recommendations.value];
      },
      category: async () => {
        const { categoryID, limit } = props.categoryData;
        return getProductsByCategoryID(categoryID, limit);
      },
      'list of sku': async () => getProductsBySku(SKUs.value, props.pageSize)
    };

    const getFetchKey = (): ProductsGroupModelFields['type'] => {
      if (props.dataProvider in productFetchers) {
        return props.dataProvider;
      }

      if (props.getProductsByCategory && props.categoryData.categoryID) {
        return 'category';
      }

      if (SKUs.value?.length && !props.getProductsByCategory) {
        return 'list of sku';
      }

      return null;
    };

    const getProducts = async () => {
      try {
        error.value = null;

        if (
          props.dataProvider !== 'algolia-recommend' &&
          !hasProductsChanged()
        ) {
          return;
        }

        const fetcherKey = getFetchKey();

        if (fetcherKey && productFetchers[fetcherKey]) {
          products.value = await productFetchers[fetcherKey]();
        }
      } catch (err) {
        error.value = err;
        Logger.error(
          '[VaimoProducts]',
          `Failed to load ${props.dataProvider} products:`,
          err
        );
      } finally {
        emit('productsLoaded', products.value?.length ?? 0);
      }
    };

    watch(SKUs, async () => {
      await getProducts();
      trackViewEvent();
    });

    onMounted(trackViewEvent);

    useFetch(async () => getProducts());

    const trackProductNavigation = (
      product: Product,
      index: number,
      event
    ): void => {
      // Prevents tracking events when clicking on the add to cart button
      if (event.target.closest('.vaimo-product-card__add-to-cart')) {
        return;
      }

      sendClickedObjectIDs([String(product.id)]);
      app.$gtm.push(getSelectItemDetails(product, index, 'slider'));
    };

    const getProductLink = (product: Product): string => {
      return app.localePath(getRelatedProductUrl(product));
    };

    const handleClickOnProduct = (product: Product, index: number, event) => {
      trackProductNavigation(product, index + 1, event);
    };

    const wrapperComponent = computed(() => {
      if (props.display === 'slider') return 'VaimoSlider';
      if (props.display === 'grid') return 'VaimoGrid';
    });

    const wrapperDisplayProps = computed(() => {
      return props.wrapperProps[props.display] || {};
    });

    const itemsLength = computed(() => {
      return products.value?.length || 0;
    });

    const wrapperClasses = computed(() => {
      if (props.display === 'grid') {
        return [
          'grid-cols-' + props.gridColsMobile,
          'm-and-l:grid-cols-' + props.gridColsDesktop
        ];
      }
      return '';
    });

    return {
      productGetters,
      customProductGetters,
      products,
      getProductLink,
      getMagentoImage,
      handleClickOnProduct,
      wrapperComponent,
      wrapperDisplayProps,
      wrapperClasses,
      itemsLength,
      hideDescriptionOptions,
      productsLoading
    };
  }
});
